import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Company, CreatedUserDTO, RegistrationCompanyData, RegistrationUserData, RegistrationVehicleDataV2, TelematicProviderType, User } from '../../types';
import { Branded } from '../../brandedType';
import { RegistrationFlowType } from "myParkdUtils/enums";
import { Sector } from 'screens/registration/InputSector';


export type TwikeyResponseStatus = Branded<'ok' | 'cancel' | 'fail' | 'pending' | 'print', 'TwikeyResponseStatus'>

export type RegistrationConfirmation = {
    registrationType: RegistrationFlowType,
    telematicProvider?: TelematicProviderType,
    numberOfCars: number
}

export type RegistrationSyncData = {
    registrationType: RegistrationFlowType,
    telematicProvider?: TelematicProviderType,
    otherTelematicProvider?: string,
    numberOfCars: number,
    sector: Sector
}

export type RegistrationContext = {
    registrationId: string,
    flowType: RegistrationFlowType,
    step: number,
    token?: string,
    createdUser?: CreatedUserDTO,
    user?: User,
    company?: Company,
    numberOfVehicles: number,
    teamleaderId?: string,
    twikeySignUrl?: string,
    twikeyPaymentType?: RegistrationPaymentType,
    telematicProviderType?: TelematicProviderType,
    otherTelematicProvider?: string,
    teamleaderSubscriptionId?: string
    sector: Sector
}
export type TwikeyValidationError =
    | { errorType: 'SignatureIncorrect', details: any }
    | { errorType: 'ResponseStatusNotValid', details: any }
    | { errorType: 'WrongRegistrationId', details: any }

export type RegistrationV2State = {
    processing: boolean;
    processed: boolean;
    registrationContext?: RegistrationContext;
    error?: any;
    processingSigning: boolean
    twikeySigningError?: TwikeyValidationError;
    twikeySignatureIsValid?: boolean,
    twikeyStatus?: TwikeyResponseStatus,
    teamleaderSubscriptionSuccessful?: boolean,
    teamleaderSubscriptionError?: any
}

export const initialRegistrationV2State: RegistrationV2State = {
    processing: false,
    processed: false,
    processingSigning: false,
    error: undefined,
    twikeySigningError: undefined,
    twikeySignatureIsValid: undefined,
    registrationContext: undefined,
    teamleaderSubscriptionSuccessful: undefined
}

export type RegistrationPaymentType = Branded<'CREDIT_CARD' | 'SEPA', 'RegistrationPaymentType'>;

export type UserRegistration = {
    user: RegistrationUserData;
    registrationCompany: RegistrationCompanyData,
    vehicles?: RegistrationVehicleDataV2[],
    numberOfCars: number,
    flowType: RegistrationFlowType,
    mandateAcceptedByUser?: boolean,
    paymentType?: RegistrationPaymentType,
    telematicProviderType?: TelematicProviderType,
    otherTelematicProvider?: string
    sector: Sector
}

export type TwikeyResponse = {
    mandateNumber: string,
    status: TwikeyResponseStatus,
    signature: string, //Parameter 's' on Twikey thank you page
    registrationId?: string //Parameter 'token' on Twikey thank you page, (optional)
}

export const registrationV2Slice = createSlice({
    name: 'registration',
    initialState: initialRegistrationV2State,
    reducers: {
        registerV2(state, action: PayloadAction<UserRegistration>) {
            state.processing = true;
            state.processed = false;
            state.registrationContext = undefined;
        },
        registerV2Completed(state, action: PayloadAction<{ registrationContext: RegistrationContext }>) {
            state.processing = false;
            state.processed = true;
            state.registrationContext = action.payload.registrationContext
            state.error = undefined;
        },
        registerV2Error(state, action: PayloadAction<{ error: any }>) {
            state.processing = false;
            state.processed = false;
            state.registrationContext = undefined;
            state.error = action.payload.error;
        },
        teamleaderSubscriptionCompleted(state, action: PayloadAction<{ registrationContext: RegistrationContext }>) {
            state.registrationContext = action.payload.registrationContext
            state.teamleaderSubscriptionSuccessful = true
        },
        teamleaderSubscriptionError(state, action: PayloadAction<{ error: any }>) {
            state.teamleaderSubscriptionSuccessful = false
            state.teamleaderSubscriptionError = action.payload.error
        },
        processTwikeyResponse(state, action: PayloadAction<TwikeyResponse>) {
            state.processingSigning = true;
            state.twikeySigningError = undefined;
            state.twikeySignatureIsValid = undefined;
        },
        onSuccessfulTwikeyResponse(state, action: PayloadAction<TwikeyResponseStatus>) {
            state.processingSigning = false;
            state.twikeySignatureIsValid = true;
            state.twikeyStatus = action.payload;
            state.twikeySigningError = undefined;
        },
        onErrorOnTwikeyResponse(state, action: PayloadAction<{ error: TwikeyValidationError }>) {
            state.processingSigning = false;
            state.twikeySignatureIsValid = false;
            state.twikeyStatus = undefined;
            state.twikeySigningError = action.payload.error;
        },
        triggerConfirmRegistration(state, action: PayloadAction<{ registrationContext?: RegistrationContext }>) { },
        reset(state, action: PayloadAction) {
            return initialRegistrationV2State;
        }
    },
    selectors: {
        getRegistrationContext: (state): RegistrationContext | undefined => state.registrationContext,
        shouldRedirectToTwikey: (state): boolean => state.registrationContext?.twikeySignUrl !== undefined,
        getTwikeySignUrl: (state): string | undefined => state.registrationContext?.twikeySignUrl
    }
})

export const {
    registerV2,
    registerV2Completed,
    registerV2Error,
    onSuccessfulTwikeyResponse
} = registrationV2Slice.actions;

export default registrationV2Slice.reducer;
